import { useState } from 'react';
import BulkTableModal from '../form/bulk-table/BulkTableModal';

type Props = {
  quarter: string;
  missing: string[];
  onDecision: (send: boolean) => Promise<void>;
  onMissingClose: () => void;
};

export default function ScsQuarterCompletionModal({
  quarter,
  missing,
  onDecision,
  onMissingClose,
}: Props) {
  const [choice, setChoice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const finish = async (send: boolean) => {
    setBusy(true);
    setError('');
    try {
      await onDecision(send);
    } catch {
      setError('Unable to save your choice. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const confirm = () => {
    if (!choice) {
      setError('Select whether to send your form for approval');
      return;
    }
    void finish(choice === 'yes');
  };
  return (
    <BulkTableModal
      title={missing.length ? `Complete your ${quarter} form` : `${quarter} complete`}
      onClose={() => {
        if (!busy) {
          if (missing.length) onMissingClose();
          else void finish(false);
        }
      }}
    >
      {missing.length ? (
        <>
          <p className="govuk-body">
            To complete your {quarter} form, you must complete the below sections:
          </p>
          <ul className="govuk-list govuk-list--bullet">
            {missing.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <button type="button" className="govuk-button" onClick={onMissingClose}>
            OK
          </button>
        </>
      ) : (
        <>
          <p className="govuk-body">
            Your Performance form is up to date and ready for approval by your line manager.
          </p>
          <p className="govuk-body">
            You can check your answers and send your form for approval on the next page.
          </p>
          {error && (
            <p className="govuk-error-message" role="alert">
              {error}
            </p>
          )}
          <fieldset className="govuk-fieldset" disabled={busy}>
            <legend className="govuk-fieldset__legend">
              Would you like to send your form for approval?
            </legend>
            <div className="govuk-radios">
              {[
                ['yes', 'Yes, send to my manager now'],
                ['no', 'No, I have more changes to make (save as incomplete)'],
              ].map(([value, label]) => (
                <div className="govuk-radios__item" key={value}>
                  <input
                    className="govuk-radios__input"
                    type="radio"
                    name="scs-send"
                    id={`scs-send-${value}`}
                    value={value}
                    checked={choice === value}
                    onChange={() => setChoice(value)}
                  />
                  <label className="govuk-label govuk-radios__label" htmlFor={`scs-send-${value}`}>
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
          <button
            type="button"
            className="govuk-button govuk-!-margin-top-4"
            disabled={busy}
            onClick={confirm}
          >
            {busy ? 'Saving...' : 'OK'}
          </button>
        </>
      )}
    </BulkTableModal>
  );
}
